// Meta Pixel — Pixel ID: 1942771500442862
//
// PRIVACY: We deliberately send MINIMAL data to Meta.
// Only anonymous funnel signals (ViewContent, AddToCart,
// InitiateCheckout, Purchase, Lead, CompleteRegistration)
// with currency, value and content_ids only.
//
// We do NOT send:
// - User email, name, or phone
// - Order IDs or internal identifiers
// - Formulation details or cluster labels
// - Any personally identifiable information
//
// This is intentional — TPC's formulation data is
// private to the user and never shared with third parties.
//
// The pixel is bootstrapped in public/index.html, which also fires PageView
// (an anonymous page hit, no payload of ours). `window.fbq` is queued by that
// snippet before the real script lands, so early calls are not lost; the guard
// below only covers the script being blocked outright (ad blocker, CSP, no
// network).
//
// Every `value` is supplied by the caller from live data — the admin sets the
// formulation price in app_settings, so there are deliberately no price
// constants in this file to drift out of step with it.

declare global {
  interface Window {
    fbq: (...args: unknown[]) => void
  }
}

export function trackEvent(
  eventName: string,
  params?: Record<string, unknown>
) {
  if (typeof window !== 'undefined' && window.fbq) {
    window.fbq('track', eventName, params)
  }
}

// Purchase must fire at most ONCE per order. Two screens can reach it — the
// in-page checkout success and the /payment/status return — and in the normal
// flow they are mutually exclusive (checkout only hands off to the status page
// when the payment is still pending). This marker closes what's left: a
// customer landing on the status page for an order checkout already counted,
// and a reload of that page while waiting.
//
// sessionStorage rather than module state, because the return from the payment
// provider can be a fresh page load. Shared from here so the two call sites
// cannot drift onto different key formats.
/**
 * Claims the right to fire Purchase for this order. True on the first call,
 * false once some screen has already counted it. Storage being unavailable
 * (private mode) returns true — a duplicate beats a missed conversion.
 */
export function claimPurchaseTracking(orderId: string): boolean {
  const key = `tpc_fbq_purchase_${orderId}`
  try {
    if (sessionStorage.getItem(key)) return false
    sessionStorage.setItem(key, '1')
    return true
  } catch {
    return true
  }
}

export const MetaPixel = {
  // User added a formula to cart. `price` is the figure the customer was
  // actually shown, from the pricing API.
  addToCart: (price: number) =>
    trackEvent('AddToCart', {
      currency: 'INR',
      value: price
    }),

  // User completed purchase. `amount` is the server-side order total.
  purchase: (amount: number) =>
    trackEvent('Purchase', {
      currency: 'INR',
      value: amount
    }),

  // A product was actually looked at — a catalogue product page, or a finished
  // formulation revealed on screen. `contentId` is the product / formulation id
  // Meta uses to match this view against a later AddToCart or Purchase; it is a
  // random internal key with no meaning outside our own database, and carries
  // nothing about the formula itself.
  viewContent: (contentId: string, price: number) =>
    trackEvent('ViewContent', {
      content_type: 'product',
      content_ids: [contentId],
      currency: 'INR',
      value: price
    }),

  // Customer asked to be told when an out-of-stock item returns. The phone
  // number they typed is deliberately NOT sent — only the fact that a restock
  // request happened, and which product it was for when we know.
  lead: (contentId?: string) =>
    trackEvent('Lead', {
      content_name: 'restock_notify',
      ...(contentId ? { content_ids: [contentId] } : {})
    }),

  // A NEW account was created. Never fired for a returning sign-in, and no
  // email, name or user id goes with it.
  completeRegistration: () =>
    trackEvent('CompleteRegistration', {
      content_name: 'quiz_signup'
    }),

  // Customer reached checkout with a non-empty bag. `total` is the payable
  // (GST-inclusive) cart total the server computed.
  initiateCheckout: (total: number, itemCount: number) =>
    trackEvent('InitiateCheckout', {
      currency: 'INR',
      value: total,
      num_items: itemCount
    })
}
